/**
 * @fileoverview useFieldEditing Hook
 * 
 * Manages state and logic for editing a single field on a detail page.
 * Handles text inputs, picker modals, and API updates.
 */

import { useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { toast } from 'react-toastify';
import { EditableFieldConfig } from '../types';

export interface UseFieldEditingProps {
  config: EditableFieldConfig;
  entity: any;
  onUpdate: (updatedEntity: any) => void;
  apiEndpoint: string;
}

export const useFieldEditing = ({
  config,
  entity,
  onUpdate,
  apiEndpoint
}: UseFieldEditingProps) => {
  const { session } = useSession();
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  /** Start editing (for text/textarea types) */
  const startEditing = useCallback(() => {
    if (config.type === 'readonly') return;

    // `date` reuses the picker-modal flow (UniversalPickerModal in
    // document-date mode), same as the helpdesk and project-parts views.
    if (config.type === 'picker' || config.type === 'date') {
      setIsPickerOpen(true);
    } else {
      const currentValue = config.extractValue(entity);
      setTextValue(typeof currentValue === 'string' ? currentValue : String(currentValue || ''));
      setIsEditing(true);
    }
  }, [config, entity]);

  /** Cancel editing */
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setIsPickerOpen(false);
    setTextValue('');
  }, []);

  /** Save text input value */
  const saveTextValue = useCallback(async () => {
    if (!textValue.trim()) {
      toast.error('Waarde mag niet leeg zijn');
      return;
    }

    if (config.validate && !config.validate(textValue)) {
      toast.error('Ongeldige waarde');
      return;
    }

    setIsUpdating(true);
    
    try {
      // Use custom update handler if provided (for text fields too)
      if (config.customUpdateHandler) {
        await config.customUpdateHandler(textValue.trim(), entity.id, session, onUpdate);
      } else {
        // Default update logic
        const payload = config.buildUpdatePayload(textValue.trim());
        const url = apiEndpoint.replace('{id}', String(entity.id));
        
        const response = await fetchWithAuth(
          url,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          },
          session
        );

        if (!response.ok) {
          throw new Error('Update failed');
        }

        const updatedEntity = await response.json();
        // Merge response over the existing entity so backends that return a
        // partial / "light" payload don't wipe fields we already had.
        onUpdate({ ...entity, ...updatedEntity });
      }

      setIsEditing(false);
      toast.success(`${config.label} bijgewerkt`);
    } catch (error) {
      console.error(`Error updating ${config.label}:`, error);
      toast.error(`Fout bij bijwerken van ${config.label}`);
    } finally {
      setIsUpdating(false);
    }
  }, [textValue, config, entity, apiEndpoint, session, onUpdate]);

  /** Handle picker selection */
  const handlePickerConfirm = useCallback(async (value: any) => {
    setIsPickerOpen(false);
    setIsUpdating(true);

    try {
      // Use custom update handler if provided
      if (config.customUpdateHandler) {
        await config.customUpdateHandler(value, entity.id, session, onUpdate);
      } else {
        // Default update logic
        const payload = config.buildUpdatePayload(value);
        const url = apiEndpoint.replace('{id}', String(entity.id));
        
        const response = await fetchWithAuth(
          url,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          },
          session
        );

        if (!response.ok) {
          throw new Error('Update failed');
        }

        const updatedEntity = await response.json();
        onUpdate({ ...entity, ...updatedEntity });
      }

      toast.success(`${config.label} bijgewerkt`);
    } catch (error) {
      console.error(`Error updating ${config.label}:`, error);
      toast.error(`Fout bij bijwerken van ${config.label}`);
    } finally {
      setIsUpdating(false);
    }
  }, [config, entity, apiEndpoint, session, onUpdate]);

  return {
    isEditing,
    isPickerOpen,
    textValue,
    isUpdating,
    setTextValue,
    startEditing,
    cancelEditing,
    saveTextValue,
    handlePickerConfirm,
    setIsPickerOpen
  };
};

